import styled from 'styled-components';
import {customScrollBarStyle, getKeyframes} from '../utils/getKeyframes';
import {

    borderRadius,
    darkBackground,
    fontFamily,
    headerSize,
    media,
    miniNavbarSize,
    mutedForeground,
    navbarMinSize,
    navbarSize,
    primaryColor,
    primaryColorTransparent,shellDivider,
  shellDividerSoft,
    sidebarBackground,
    textColorDark,
} from '../utils/variables';
import { Image } from './image';
import {Text} from './text';
import {iconActive, iconHover, iconMuted} from './iconStyles';
import { Col } from 'antd';

interface NavItemProps {
    isActive?: boolean;
    isNavOpen?: boolean;
}

const navIconBase = `
    transition: filter 200ms ease, opacity 200ms ease;
`;

const navIconMuted = `
    ${navIconBase}
    ${iconMuted}
`;

const navIconHover = `
    ${navIconBase}
    ${iconHover}
`;

const navIconActive = `
    ${navIconBase}
    ${iconActive}
    opacity: 1 !important;
`;

const navItemActiveStyles = `
    color: ${primaryColor};
    background-color: ${primaryColorTransparent};
    font-weight: 600;

    ${Image} {
        ${navIconActive}
    }

    &:hover {
        background-color: ${primaryColorTransparent};
        color: ${primaryColor};

        ${Image} {
            ${navIconActive}
        }
    }
`;

const navChildItemActiveStyles = `
    color: ${primaryColor};
    background-color: ${primaryColorTransparent};
    font-weight: 600;

    &:hover {
        background-color: ${primaryColorTransparent};
        color: ${primaryColor};
    }
`;

export const NavList = styled.ul<{
    burgerOpen?: boolean;
}>`
    list-style: none;
    padding: 4px 0 8px;
    margin: 0;
    box-sizing: border-box;
    min-height: min-content;
    overflow-x: hidden;
    overflow-y: visible;

  ${media.mobile} {
    flex-flow: column nowrap;
    background-color: ${sidebarBackground};
    border-right: 1px solid ${shellDivider};
    position: fixed;
    transform: ${({burgerOpen}) => burgerOpen ? `translateX(0)` : `translateX(-100%)`};
    top: var(--billing-banner-height, 0px);
    left: 0;
    height: calc(100dvh - var(--billing-banner-height, 0px));
    min-height: 0;
    max-height: calc(100dvh - var(--billing-banner-height, 0px));
    width: 75%;
    padding-top: 50px;
    transition: transform 0.3s ease-in-out;
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior: contain;
    touch-action: pan-y;
    -webkit-overflow-scrolling: touch;
    z-index: 19;
  }

  ${media.tablet} {
    flex-flow: column nowrap;
    background-color: ${sidebarBackground};
    border-right: 1px solid ${shellDivider};
    position: fixed;
    transform: ${({burgerOpen}) => burgerOpen ? `translateX(0)` : `translateX(-100%)`};
    top: var(--billing-banner-height, 0px);
    left: 0;
    height: calc(100dvh - var(--billing-banner-height, 0px));
    min-height: 0;
    max-height: calc(100dvh - var(--billing-banner-height, 0px));
    width: 280px;
    padding-top: 50px;
    transition: transform 0.3s ease-in-out;
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior: contain;
    touch-action: pan-y;
    -webkit-overflow-scrolling: touch;
    z-index: 19;
  }
`;

const navItemBase = `
    border-radius: 8px;
    transition: 200ms ease;
    color: ${mutedForeground};
    cursor: pointer;

    ${Image} {
        ${navIconMuted}
        flex-shrink: 0;
    }

    &:hover {
        background-color: var(--nav-item-hover-bg);
        color: ${textColorDark};

        ${Image} {
            ${navIconHover}
        }
    }
`;

export const NavItem = styled.div<NavItemProps>`
    ${navItemBase}
    padding: 10px 12px;
    margin: 2px 6px;

    ${({ isActive }) => isActive ? navItemActiveStyles : ``}
`;

export const NavItemRow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
`;

export const NavItemCompact = styled.div<NavItemProps>`
    ${navItemBase}
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 8px 4px;
    margin: 2px 4px;
    text-align: center;
    min-height: 56px;

    ${({ isActive }) => isActive ? navItemActiveStyles : ``}
`;

export const NavMiniLabel = styled.span`
    font-family: ${fontFamily};
    font-size: 9px;
    line-height: 1.15;
    font-weight: 500;
    color: inherit;
    max-width: 100%;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    word-break: break-word;
    hyphens: auto;
`;

export const NavGroupHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
    flex: 1;
    font-size: 14px;
    font-family: ${fontFamily};
    color: inherit;
`;

export const NavFlyoutTitle = styled.div`
    font-size: 12px;
    font-weight: 600;
    font-family: ${fontFamily};
    color: ${mutedForeground};
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 4px 10px 8px;
    border-bottom: 1px solid ${shellDividerSoft};
    margin-bottom: 4px;
`;

export const NavFlyoutList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 180px;
    max-width: 240px;
`;

export const NavBody = styled.div<{ isMacOs?: boolean }>`
    flex: 1;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
    ${({isMacOs}) => customScrollBarStyle(isMacOs!)}
`;

export const NavToggle = styled.button<{ expanded?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: calc(100% - 12px);
    margin: 4px 6px 8px;
    padding: 8px 0;
    border: 1px solid ${shellDivider};
    border-radius: ${borderRadius};
    background: ${sidebarBackground};
    cursor: pointer;
    flex-shrink: 0;
    transition: 200ms ease;

    ${Image} {
        ${navIconMuted}
        width: 18px;
        height: 18px;
        transform: rotate(${({ expanded }) => expanded ? '180deg' : '0deg'});
        transition: transform 250ms ease, filter 200ms ease;
    }

    &:hover {
        border-color: ${primaryColor};
        background-color: ${primaryColorTransparent};

        ${Image} {
            ${navIconActive}
        }
    }

    ${media.mobile} {
        display: none;
    }

    ${media.tablet} {
        display: none;
    }
`;

/** @deprecated use NavToggle */
export const Arrow = styled(Image)`
    display: none;
`;

export const Nav = styled.nav<{
    expanded?: boolean;
    collapsed?: boolean;
    burgerOpen?: boolean;
    isMacOs?: boolean;
}>`
    display: flex;
    flex-direction: column;
    width: ${({ expanded }) => expanded ? navbarSize : miniNavbarSize};
    min-width: ${({ expanded }) => expanded ? navbarMinSize : miniNavbarSize};
    height: 100vh;
    background-color: ${sidebarBackground};
    border-right: 1px solid ${shellDivider};
    position: fixed;
    left: 0;
    top: var(--billing-banner-height, 0px);
    transition: 250ms;
    position: relative;
  
    overflow: hidden;
    //max-height: fit-content;

  ${({ expanded, collapsed }) => !(expanded ?? collapsed)
          ? `
            ${NavItem} {
                display: flex;
                justify-content: center;
                align-items: center;
            }
            ${Text} {
                display: none;
            }
        `
          : ``
  };

    > * {
        opacity: 0;
        animation: fadeIn 500ms forwards;
    }

    ${media.mobile} {
        display: ${({burgerOpen}) => burgerOpen ? `flex` : `none`};
    }

    ${media.tablet} {
        display: ${({burgerOpen}) => burgerOpen ? `flex` : `none`};
    }

    ${getKeyframes('fadeIn')}
`;

export const NavChildItem = styled.div<NavItemProps>`
    display: block;
    color: ${mutedForeground};
    padding: 8px 10px;
    border-radius: 6px;
    margin: 0 2px;
    font-size: 14px;
    font-family: ${fontFamily};
    line-height: 1.35;
    text-decoration: none;
    transition: 200ms ease;

    &:hover {
        color: ${textColorDark};
        background-color: var(--nav-item-hover-bg);
    }

    ${({ isActive }) => isActive ? navChildItemActiveStyles : ``}

    ${({ isNavOpen }) => isNavOpen
        ? `padding-left: 12px;`
        : ``
    };
`;

export const StyledBadge = styled(Col)<NavItemProps>`
    width: 100% !important;
    max-width: 100%;

    ${Image} {
        ${navIconMuted}
    }

    .ant-collapse {
        border: none;
        background: transparent;
    }

    .ant-collapse-item {
        border-bottom: none !important;
    }

    .ant-collapse-content {
        border-top: none !important;
        background: transparent !important;
    }

    .ant-collapse .ant-collapse-content > .ant-collapse-content-box {
        background-color: ${sidebarBackground};
        padding: 0 0 4px 8px !important;
    }

    .ant-collapse-header {
        align-items: center !important;
        border-radius: 8px !important;
        margin: 2px 6px !important;
        padding: 10px 12px !important;
        color: ${mutedForeground} !important;
        transition: background-color 200ms ease, color 200ms ease;

        ${Image} {
            ${navIconMuted}
        }

        &:hover {
            background-color: var(--nav-item-hover-bg) !important;
            color: ${textColorDark} !important;

            ${Image} {
                ${navIconHover}
            }
        }
    }

    .ant-collapse-header-text {
        flex: 1;
        min-width: 0;
        margin-inline-start: 0 !important;
    }

    .ant-collapse-expand-icon {
        position: static !important;
        padding-inline-start: 8px !important;
        color: ${mutedForeground} !important;
    }

    ${({ isActive }) => isActive
        ? `
            .ant-collapse-item-active > .ant-collapse-header {
                color: ${textColorDark} !important;

                ${Image} {
                    ${navIconHover}
                }
            }
        `
        : ``
    }

    ${({ isActive }) => isActive
        ? `
            .ant-collapse-header {
                color: ${primaryColor} !important;
                background-color: ${primaryColorTransparent} !important;

                ${Image} {
                    ${navIconActive}
                }

                &:hover {
                    color: ${primaryColor} !important;
                    background-color: ${primaryColorTransparent} !important;

                    ${Image} {
                        ${navIconActive}
                    }
                }
            }

            .ant-collapse-expand-icon {
                color: ${primaryColor} !important;
            }
        `
        : ``
    }
`;
export const UL = styled.li`
  list-style-type: none; 
  color: white;
  padding-left: 70px !important;
  padding: 10px;
  &:hover {
    opacity: .8;
    background-color: rgba(0,0,0,.1);
}
`;
